import React, { useEffect, useState } from 'react';
import { BarChart3, CheckCircle, Clock, RefreshCw, TrendingUp, PieChart } from 'lucide-react';
import { getActivityLaporan } from '../../api';

export default function LaporanActivity() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    getActivityLaporan()
      .then(res => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const total = data?.total || 0;
  const completed = data?.completed || 0;
  const inProgress = data?.in_progress || 0;
  const pending = data?.pending || 0;
  // Colors for charts
  const COLORS = ['#059669', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <BarChart3 size={24} />
              Activity Reports
            </h2>
            <p className="page-desc">Ringkasan dan statistik activity</p>
          </div>
          <button className="btn btn-secondary" onClick={load} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spinner' : ''} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="page-loading">Memuat data...</div>
      ) : !data ? (
        <div className="page-loading">Tidak ada data</div>
      ) : (
        <>
          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 24 }}>
            <div style={{ background: '#f0fdf4', borderRadius: 12, padding: '16px 20px', border: '1px solid #bbf7d0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <BarChart3 size={20} style={{ color: '#16a34a' }} />
                </div>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#166534', fontWeight: 500 }}>Total Activities</div>
                  <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#166534' }}>{total}</div>
                </div>
              </div>
            </div>
            <div style={{ background: '#f0fdf4', borderRadius: 12, padding: '16px 20px', border: '1px solid #bbf7d0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CheckCircle size={20} style={{ color: '#16a34a' }} />
                </div>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#166534', fontWeight: 500 }}>Completed</div>
                  <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#166534' }}>{completed}</div>
                </div>
              </div>
            </div>
            <div style={{ background: '#fefce8', borderRadius: 12, padding: '16px 20px', border: '1px solid #fde68a' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Clock size={20} style={{ color: '#d97706' }} />
                </div>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#92400e', fontWeight: 500 }}>In Progress</div>
                  <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#92400e' }}>{inProgress}</div>
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
            {/* Category Distribution */}
            <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#374151', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <PieChart size={16} /> Category Distribution
              </h3>
              {(data.categories || []).length === 0 ? (
                <div style={{ color: '#9ca3af', fontSize: '0.85rem', textAlign: 'center', padding: 20 }}>Belum ada data</div>
              ) : (
                <div>
                  {data.categories.map((c, i) => {
                    const pct = total > 0 ? Math.round((c.count / total) * 100) : 0;
                    return (
                      <div key={c.category} style={{ marginBottom: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: '0.82rem' }}>
                          <span style={{ fontWeight: 500 }}>{c.category}</span>
                          <span style={{ color: '#6b7280' }}>{c.count} ({pct}%)</span>
                        </div>
                        <div style={{ background: '#f3f4f6', borderRadius: 6, height: 8, overflow: 'hidden' }}>
                          <div style={{
                            width: `${pct}%`, height: '100%',
                            background: COLORS[i % COLORS.length],
                            borderRadius: 6, transition: 'width 0.5s ease'
                          }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Status Distribution */}
            <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#374151', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <TrendingUp size={16} /> Status Overview
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  { label: 'Completed', count: completed, color: '#059669', bg: '#d1fae5' },
                  { label: 'In Progress', count: inProgress, color: '#2563eb', bg: '#dbeafe' },
                  { label: 'Pending', count: pending, color: '#d97706', bg: '#fef3c7' },
                ].map(s => {
                  const pct = total > 0 ? Math.round((s.count / total) * 100) : 0;
                  return (
                    <div key={s.label} style={{ padding: '12px 16px', borderRadius: 10, background: s.bg }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: '0.8rem', fontWeight: 600, color: s.color }}>{s.label}</div>
                          <div style={{ fontSize: '1.3rem', fontWeight: 800, color: s.color }}>{s.count}</div>
                        </div>
                        <div style={{ fontSize: '1.2rem', fontWeight: 700, color: s.color, opacity: 0.6 }}>{pct}%</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Priority Distribution */}
            <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#374151', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <BarChart3 size={16} /> Priority Distribution
              </h3>
              {(data.priorities || []).length === 0 ? (
                <div style={{ color: '#9ca3af', fontSize: '0.85rem', textAlign: 'center', padding: 20 }}>Belum ada data</div>
              ) : (
                <div>
                  {data.priorities.map((p, i) => (
                    <div key={p.priority} style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: '0.82rem' }}>
                        <span style={{ fontWeight: 500 }}>{p.priority}</span>
                        <span style={{ color: '#6b7280' }}>{p.count}</span>
                      </div>
                      <div style={{ background: '#f3f4f6', borderRadius: 6, height: 8, overflow: 'hidden' }}>
                        <div style={{
                          width: `${total > 0 ? (p.count / total) * 100 : 0}%`, height: '100%',
                          background: ['#f59e0b', '#ef4444', '#6b7280', '#dc2626'][i % 4],
                          borderRadius: 6, transition: 'width 0.5s ease'
                        }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Bagian Distribution */}
            <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#374151', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <BarChart3 size={16} /> Bagian Distribution
              </h3>
              {(data.bagians || []).length === 0 ? (
                <div style={{ color: '#9ca3af', fontSize: '0.85rem', textAlign: 'center', padding: 20 }}>Belum ada data</div>
              ) : (
                <div>
                  {data.bagians.map((b, i) => (
                    <div key={b.bagian} style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: '0.82rem' }}>
                        <span style={{ fontWeight: 500 }}>{b.bagian}</span>
                        <span style={{ color: '#6b7280' }}>{b.count}</span>
                      </div>
                      <div style={{ background: '#f3f4f6', borderRadius: 6, height: 8, overflow: 'hidden' }}>
                        <div style={{
                          width: `${total > 0 ? (b.count / total) * 100 : 0}%`, height: '100%',
                          background: COLORS[i % COLORS.length],
                          borderRadius: 6, transition: 'width 0.5s ease'
                        }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Monthly Trend */}
            <div style={{ gridColumn: '1 / -1', background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#374151', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <TrendingUp size={16} /> Monthly Trend (6 months)
              </h3>
              {(data.monthly || []).length === 0 ? (
                <div style={{ color: '#9ca3af', fontSize: '0.85rem', textAlign: 'center', padding: 20 }}>Belum ada data</div>
              ) : (
                <div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, height: 160, padding: '0 10px' }}>
                    {data.monthly.map((m, i) => {
                      const maxCount = Math.max(...data.monthly.map(x => x.total), 1);
                      const height = (m.total / maxCount) * 140;
                      return (
                        <div key={m.bulan} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#059669' }}>{m.total}</span>
                          <div style={{
                            width: '100%', maxWidth: 60, height: Math.max(height, 4),
                            background: '#059669', borderRadius: '6px 6px 0 0',
                            opacity: 0.7 + (i / data.monthly.length) * 0.3
                          }} />
                          <span style={{ fontSize: '0.65rem', color: '#6b7280', fontWeight: 500 }}>
                            {m.bulan?.slice(-2)}/{m.bulan?.slice(0, 4)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
